import { Button } from "@/src/components/buttons";
import { InfiniteFetchableList } from "@/src/components/fetchableLists";
import { Patient, PatientLight } from "@/src/data/allTypes";
import { USER } from "@/src/data/globalVariables";
import apiFetch, { sleep } from "@/src/utils/apiFetch";
import { useRouterHook } from "@/src/utils/routerHook";
import {
  getItemId,
  getServiceId,
  getUserId,
  hasPermission,
  isMobile,
  isNurse,
  uid,
} from "@/src/utils/utils";
import { useEffect, useState } from "react";
import { FaRegFolderOpen } from "react-icons/fa";
import { closeDrawer, openDrawer } from "../../components/dialogs";
import { openConsultationPage } from "../consultation/consultationPage";
import {
  openHospitalisationDrawer,
  openNurseHospitalisation,
} from "../hospitalisation/hospitalisationHook";
import { BarcodeReader } from "../pharmacie/stocks/barcodeReader";
import { CardIdReaderButton } from "./cardIdReader";
import { hasAccessToPatient } from "./confidentialitiesHook";
import { handlePatientCreationError } from "./patientCreationErrorHandling";
import { PatientDashboard } from "./patientDashboard";
import { PatientIdentityForm } from "./patientIdentityForm";
import { PatientsListItem } from "./patientsListItem";
import SearchTips from "./searchTips";
import { SimplifiedPatientIdentityForm } from "./simplifiedPatientIdentityForm";

function PatientsPage() {
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [reload, setReload] = useState(0);
  const [historyType, setHistoryType] = useState<string | null>(null);
  const { getParamFromUrl } = useRouterHook();

  useEffect(() => {
    loadSelectedPatient();
    openSelectedConsultation();
    openSelectedHospitalisation();
  }, []);

  async function loadSelectedPatient() {
    const patientId = getParamFromUrl("patientId", "patients");
    if (!patientId) return;
    const patient = await apiFetch("patients", "loadOne", {
      id: patientId,
    });
    if (!patient) return;

    setSelectedPatient(patient);
  }

  async function openSelectedConsultation() {
    const consultationId = getParamFromUrl("consultationId", "consultations");
    if (!consultationId) return;
    const consultation = await apiFetch("consultations", "loadById", {
      id: consultationId,
    });
    const patient = await apiFetch("patients", "loadOne", {
      id: getItemId(consultation?.patient),
    });
    if (!patient) return;
    openConsultationPage({
      consultationId,
      patient,
      onSave: () => {},
    });
  }

  async function openSelectedHospitalisation() {
    const hospitalisationId = getParamFromUrl("hospitalisationId", "hospitalisations");
    if (!hospitalisationId) return;
    if (hasPermission("nurseView") || isNurse(USER.get()))
      return await openNurseHospitalisation(hospitalisationId);
    await openHospitalisationDrawer({
      itemId: hospitalisationId,
      onImmediateDischarge: async () => {},
      onConfirm: async (hospitalisation) => {
        setReload(reload + 1);
      },
    });
    setReload(reload + 1);
  }

  return (
    <div id="patients_page">
      <PatientsList
        key={`patientsList${reload}`}
        selectedPatient={selectedPatient}
        onSelect={async (patient) => {
          setSelectedPatient(patient);
          if (isMobile())
            await openDrawer({
              id: "patientDrawer",
              title: "Patient",
              additionalClasses: ["noPadding"],
              content: (
                <PatientDashboard
                  key={uid()}
                  currentPatient={patient}
                  onChange={(newValue) => {
                    setSelectedPatient(newValue);
                    setReload(reload + 1);
                  }}
                ></PatientDashboard>
              ),
            });
        }}
      ></PatientsList>

      {!selectedPatient?.id && !isMobile() && (
        <div className="gray-bg center tcenter">
          <div style={{ opacity: "0.8" }}>
            <FaRegFolderOpen size={60} />
            <div className="bold mtop5">Aucun patient Choisi</div>
            <div className="mtop5 gray">Veuillez choisir un patient</div>
          </div>
        </div>
      )}

      {selectedPatient?.id && !isMobile() && (
        <div className=" gray-bg">
          <PatientDashboard
            key={uid()}
            currentPatient={selectedPatient}
            historyType={historyType}
            onHistoryTypeChange={(newValue) => {
              setHistoryType(newValue);
            }}
            onChange={(newValue) => {
              setSelectedPatient(newValue);
              setReload(reload + 1);
            }}
          ></PatientDashboard>
        </div>
      )}
    </div>
  );
}

function PatientsList({
  initialSearchKey,
  selectedPatient,
  useSimplifiedIdentityForm,
  onSelect,
}: {
  initialSearchKey?: string | null | undefined;
  selectedPatient: Patient | null | undefined;
  useSimplifiedIdentityForm?: boolean;
  onSelect?: (patient: Patient) => void;
}) {
  const [enableFuzzyMatching, setEnableFuzzyMatching] = useState(false);
  const [reload, setReload] = useState(0);
  const [searchKey, setSearchKey] = useState(initialSearchKey || "");

  async function openPatientIdentityForm(
    initialData: Partial<Patient> | null | undefined
  ) {
    let searchParts = searchKey?.split(" ") || [];
    let firstName = searchParts[1] || "";
    let lastName = searchParts[0] || "";

    let patient: Patient = {
      lastName: lastName || "",
      firstName: firstName || "",
      birth: initialData?.birth || "",
      familyMembers: [],
      creator: getUserId(),
      confidentiality: null,
      services: [getServiceId()],
      ...initialData,
    };

    await openDrawer({
      id: "patientDrawer",
      title: "Patient",
      additionalClasses: ["big", "whiteBody"],
      content:
        useSimplifiedIdentityForm == true ? (
          <SimplifiedPatientIdentityForm
            initialValue={patient}
            onChange={(newValue) => {
              patient = newValue;
            }}
          />
        ) : (
          <PatientIdentityForm
            initialValue={patient}
            onChange={(newValue) => {
              patient = newValue;
            }}
          />
        ),
      onConfirm: async (e) => {
        try {
          let result = await apiFetch("patients", "addOne", {
            patient,
            serviceId: getServiceId(),
            userId: getUserId(),
          });
          if (!result) return;
          onSelect?.(result);
          setReload(reload + 1);
        } catch (e: any) {
          console.log("eeerr error", e.message);
          await handlePatientCreationError({
            patient,
            errorMessage: e.message,
            onSelect: (patient) => {
              onSelect?.(patient);
              setReload(reload + 1);
            },
          });
        }
      },
    });
  }

  return (
    <div id="patients_list_container">
      {/* <Button
        onClick={async () => {
          let result = await apiFetchStream("general", "loadStream", {
            serviceId: null,
            searchKey: "test",
          });
          console.log("result", result);
          decodeStream({
            stream: result,
            onChunkReceived: (chunk, fullValue) => {
              console.log("chunk", chunk, "fullValue", fullValue);
            },
            onStreamComplete: (fullValue) => {
              console.log("fullValue", fullValue);
            },
          });
        }}
      >
        test
      </Button> */}
      {/* <CardIdReaderButton
        onSelect={async (patient) => {
          onSelect?.(patient);
        }}
      ></CardIdReaderButton> */}

      <BarcodeReader
        onScan={async (parsedBarcode) => {
          console.log("parsedBarcode", parsedBarcode);
          if (parsedBarcode.barcodeCategory != "patient") return;
          closeDrawer("patientDrawer");
          const patient = await apiFetch("patients", "loadOneByNameAndBirth", {
            firstName: parsedBarcode.firstName || "",
            lastName: parsedBarcode.lastName || "",
            birth: parsedBarcode.birth || "",
          });

          if (patient) {
            setSearchKey("");
            setReload((prev) => prev + 1);
            return await onSelect?.(patient);
          }

          await sleep(150);
          openPatientIdentityForm({
            lastName: parsedBarcode.lastName || "",
            firstName: parsedBarcode.firstName || "",
            birth: parsedBarcode.birth || "",
          });
          await sleep(50);
          setSearchKey("");
          setReload((prev) => prev + 1);
        }}
        allowCamera={false}
      ></BarcodeReader>
      <InfiniteFetchableList
        key={`patientsList-${reload}`}
        limit={20}
        shouldFocus={true}
        getFunction={async (limit, offset, searchKey) => {
          return (await apiFetch("patients", "loadMany", {
            serviceId: searchKey ? null : getServiceId(),
            // serviceId: null,
            searchKey,
            enableFuzzyMatching,
            offset,
            limit,
            includedFields: [
              "id",
              "firstName",
              "lastName",
              "birth",
              "confidentiality",
              "services",
              "searchInfo",
              "searchInfoAr",
              "_date",
            ],
          })) as PatientLight[];
        }}
        queryKey={["patients", reload, searchKey, enableFuzzyMatching]}
        emptyChild={
          <div className="c1 gap10 center p30">
            <div className="gray">Aucun patient trouvé</div>

            {/* {enableFuzzyMatching != true && searchKey && (
              <div className="mbottom20">
                <Button
                  type="tertiary"
                  onClick={async () => {
                    setEnableFuzzyMatching(true);
                  }}
                >
                  Utiliser Recherche approfondie
                </Button>
              </div>
            )} */}

            <Button
              onClick={async () => {
                await openPatientIdentityForm({});
              }}
            >
              Créer le patient
            </Button>
            <div className="gray">Ou</div>
            <CardIdReaderButton
              onSelect={async (patient) => {
                await onSelect?.(patient);
              }}
            ></CardIdReaderButton>
            <SearchTips searchKey={searchKey} />
          </div>
        }
        onClick={async () => {
          await openPatientIdentityForm({});
        }}
        searchKey={searchKey}
        onSearch={(newValue) => {
          setEnableFuzzyMatching(false);
          setSearchKey(newValue || "");
        }}
      >
        {(patients) => {
          let userPatients =
            patients?.filter((patient) => {
              return hasAccessToPatient(patient);
            }) || [];

          let otherPatients =
            patients?.filter((patient) => {
              return !hasAccessToPatient(patient);
            }) || [];

          return (
            <div className="c1 gap5">
              {userPatients.map((patient) => {
                if (!patient) return null;
                return (
                  <PatientsListItem
                    key={patient.id}
                    patient={patient}
                    selectedPatient={selectedPatient}
                    onSelect={onSelect}
                    searchKey={searchKey}
                  ></PatientsListItem>
                );
              })}

              {otherPatients.length != 0 && (
                <div className="c1 gap5" style={{ opacity: 0.8 }}>
                  <div className="bold mtop10 mbottom5 mleft5">
                    Autres patients
                  </div>
                  {otherPatients.map((patient) => {
                    if (!patient) return null;
                    return (
                      <PatientsListItem
                        key={patient.id}
                        patient={patient}
                        selectedPatient={selectedPatient}
                        onSelect={onSelect}
                        searchKey={searchKey}
                      ></PatientsListItem>
                    );
                  })}
                </div>
              )}
            </div>
          );
        }}
      </InfiniteFetchableList>
    </div>
  );
}

export { PatientsList, PatientsPage };
